import React from 'react';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/20/solid';
import { formatSignedPercent } from './format';

export const Card = ({ as: Component = 'section', className = '', children, ...props }) => (
  <Component
    className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800/70 dark:ring-white/10 sm:p-5 ${className}`}
    {...props}
  >
    {children}
  </Component>
);

export const SectionHeader = ({ icon: Icon, title, subtitle, action, className = '' }) => (
  <div className={`mb-3 flex items-center justify-between gap-3 ${className}`}>
    <div className="flex min-w-0 items-center gap-3">
      {Icon && (
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400">
          <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
      )}
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
        {subtitle && <p className="truncate text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
      </div>
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

/**
 * Change vs. the comparison period: the percentage when the baseline allows one, otherwise the
 * absolute difference through `formatAbs`.
 */
export const DeltaPill = ({ delta, formatAbs, className = '' }) => {
  if (!delta) return null;
  const usePct = delta.pct !== null && delta.pct !== undefined;
  const value = usePct ? delta.pct : delta.abs;
  const direction = Math.abs(value) < 1e-9 ? 'flat' : value > 0 ? 'up' : 'down';
  const tone = {
    up: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
    down: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
    flat: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300'
  }[direction];
  const Icon = direction === 'down' ? ArrowTrendingDownIcon : ArrowTrendingUpIcon;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${tone} ${className}`}>
      {direction !== 'flat' && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {usePct ? formatSignedPercent(value) : formatAbs(value)}
    </span>
  );
};

export const LegendDot = ({ className = '', style }) => (
  <span className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${className}`} style={style} aria-hidden="true" />
);

// Cool tones only, so avatars never read as the green/red of GGR or the violet/amber of the verticals
const AVATAR_TONES = [
  'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300'
];

const initials = (name) => {
  const words = String(name || '?').trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0][0]}${words[1][0]}` : (words[0] || '?').slice(0, 2);
  return letters.toUpperCase();
};

const toneFor = (seed) => {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

export const Avatar = ({ name, seed }) => (
  <span
    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${toneFor(seed || name)}`}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

export const SegmentedControl = ({ options, value, onChange, label, className = '', buttonClassName = 'px-3 py-1.5 text-xs sm:text-sm' }) => (
  <div role="radiogroup" aria-label={label} className={`inline-flex rounded-xl bg-gray-100 p-1 dark:bg-gray-900/60 ${className}`}>
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={`flex-1 touch-manipulation whitespace-nowrap rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
            selected
              ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
              : 'bg-transparent text-gray-500 hover:text-gray-800 dark:bg-transparent dark:text-gray-400 dark:hover:text-gray-200'
          } ${buttonClassName}`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-gray-200/80 motion-reduce:animate-none dark:bg-gray-700/60 ${className}`} />
);

export const EmptyState = ({ title, className = '' }) => (
  <p className={`py-6 text-center text-sm text-gray-400 dark:text-gray-500 ${className}`}>{title}</p>
);

export const InlineError = ({ children }) => (
  <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">{children}</p>
);

export const TextButton = ({ children, className = '', ...props }) => (
  <button
    type="button"
    className={`text-xs font-semibold text-teal-600 hover:text-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-teal-400 dark:hover:text-teal-300 ${className}`}
    {...props}
  >
    {children}
  </button>
);

/**
 * Optional avatar, name, and a figure on the far side; a button when `onClick` is given. Lists of
 * rows sit in a `-mx-2` wrapper so the hover background lines up with the card's padding.
 */
export const ListRow = ({ leading, label, hint, aside, value, valueClassName = 'text-gray-900 dark:text-white', onClick }) => {
  const content = (
    <>
      {leading}
      <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
        {label}
        {hint && <span className="ms-1.5 text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
      </span>
      <span className="flex flex-shrink-0 items-baseline gap-2">
        {aside && <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">{aside}</span>}
        <span className={`text-sm font-semibold tabular-nums ${valueClassName}`}>{value}</span>
      </span>
    </>
  );

  const className = `flex w-full items-center gap-3 rounded-lg px-2 text-start ${leading ? 'py-2' : 'py-2.5'}`;
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${className} touch-manipulation transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-gray-100 dark:hover:bg-gray-700/40 dark:active:bg-gray-700/60`}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
};
